import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  RISKFLOW_SOURCE_FILTERS,
  type RiskFlowSourceFilter,
} from "../../lib/riskflow-source-filter";
import { DropdownSurface } from "./DropdownSurface";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

interface SourceFilterMenuProps {
  selected: Set<RiskFlowSourceFilter>;
  onToggle: (source: RiskFlowSourceFilter) => void;
  counts?: Partial<Record<RiskFlowSourceFilter, number>>;
}

export function SourceFilterMenu({
  selected,
  onToggle,
  counts,
}: SourceFilterMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const safeSelected = useMemo(
    () =>
      selected.size > 0
        ? selected
        : new Set<RiskFlowSourceFilter>(RISKFLOW_SOURCE_FILTERS),
    [selected],
  );

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        ref.current &&
        !ref.current.contains(target) &&
        !surfaceRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const summary =
    safeSelected.size === RISKFLOW_SOURCE_FILTERS.length
      ? "Source: All"
      : `Source: ${Array.from(safeSelected).join(", ")}`;

  return (
    <div className="relative" ref={ref}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 rounded border bg-[var(--fintheon-bg)] px-1.5 py-0.5 text-[10px] transition-colors focus:outline-none ${
          open || safeSelected.size < RISKFLOW_SOURCE_FILTERS.length
            ? "border-[var(--fintheon-accent)]/40 text-[var(--fintheon-accent)]"
            : "border-zinc-800 text-zinc-400 hover:text-zinc-300"
        }`}
        title="Filter by source"
      >
        <span>{summary}</span>
        <ChevronDown
          className={`h-2.5 w-2.5 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      <DropdownSurface
        open={open}
        anchorRef={buttonRef}
        surfaceRef={surfaceRef}
        minWidth={156}
      >
        {RISKFLOW_SOURCE_FILTERS.map((source) => {
          const isOn = safeSelected.has(source);
          const isLocked = isOn && safeSelected.size === 1;
          const count = counts?.[source];
          return (
            <button
              key={source}
              type="button"
              disabled={isLocked}
              onClick={() => {
                if (!isLocked) onToggle(source);
              }}
              className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-[10px] transition-colors ${
                isOn
                  ? "bg-[var(--fintheon-accent)]/5 text-[var(--fintheon-accent)]"
                  : "text-[var(--fintheon-text)]/52 hover:bg-[var(--fintheon-accent)]/[0.075] hover:text-[var(--fintheon-text)]/82"
              } ${isLocked ? "cursor-not-allowed opacity-60" : ""}`}
              title={
                isLocked
                  ? "Select another source before disabling this one"
                  : undefined
              }
            >
              <span
                className={`inline-flex h-3 w-3 items-center justify-center border ${
                  isOn
                    ? "border-[var(--fintheon-accent)]/60"
                    : "border-[var(--fintheon-text)]/18"
                }`}
              >
                {isOn && (
                  <Check className="h-2.5 w-2.5 text-[var(--fintheon-accent)]" />
                )}
              </span>
              <span className="flex-1 text-left tracking-wider">{source}</span>
              {count != null && count > 0 && (
                <span className="tabular-nums text-[var(--fintheon-text)]/30">
                  <FintheonNumericText text={count} kind="count" />
                </span>
              )}
            </button>
          );
        })}
      </DropdownSurface>
    </div>
  );
}
